import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetUserInviteFriendsListQuery } from '@api/queries/user';
import { type GetUserInviteFriendsRequestType } from '@common/types/query/user';
import { StringSmall, StringXSmall } from '@components/Typography/Typography';
import { MainButton } from '@components/ui/Button/Button';
import { CashImage } from '@components/ui/CashImage/CashImage';
import cn from 'classnames';

import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { Invite } from '../Invite/Invite';
import { NoInvitePartners } from '../NoInvitePartners/NoInvitePartners';

import { FriendDataRow } from './FriendDataRow';

import css from './styles.module.scss';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface FriendsTableProps {
  level: number;
}

const FriendsTable: FC<FriendsTableProps> = ({ level }) => {
  const { t } = useTranslation();
  const page = JSON.parse(
    sessionStorage.getItem('numberPagePartnersTable') || '2',
  );
  const invitedFriends = useAppSelector(
    (state) => state.user.referralEarnings?.invitedFriendsCount,
  );
  const [queryRequestData, setQueryRequestData] =
    useState<GetUserInviteFriendsRequestType>({
      limit: 10,
      referral_level: level,
    });
  const { data, isLoading } =
    useGetUserInviteFriendsListQuery(queryRequestData);
  useEffect(() => {
    if (!data?.length) sessionStorage.setItem('numberPagePartnersTable', '2');
  }, [data]);

  const showMore = (): void => {
    if (data && !!data.length) {
      setQueryRequestData({
        limit: 10,
        referral_level: level,
        page,
      });
      sessionStorage.setItem('numberPagePartnersTable', page + 1);
    }
  };

  const totalCash = data?.reduce((prev, curr) => prev + curr.totalCash, 0) || 0;
  const totalDice = data?.reduce((prev, curr) => prev + curr.totalDices, 0);

  return (
    <div className={css.friendsTable}>
      <div className={cn(css.tableHead, css.columnTemplate)}>
        <div className="flex items-center gap-1">
          <StringXSmall weight="medium" className="!text-white/60">
            {`${t('Tier')} ${level} ${t('Partners')}:`}
          </StringXSmall>
          <StringSmall weight="bold">{invitedFriends}</StringSmall>
        </div>

        <div className="flex items-center gap-1 justify-self-end">
          <CashImage size={32} className="justify-self-end" />
          <StringSmall weight="bold">{totalCash}</StringSmall>
        </div>
        <div className="flex items-center gap-1 justify-self-end">
          <Picture
            className="w-[32px] h-[32px]"
            img={tgemPng}
            webp={tgemWebp}
            alt="tgem"
          />
          <StringSmall weight="bold">{totalDice}</StringSmall>
        </div>
      </div>

      <div className="w-full pt-3 h-[27.5rem] overflow-y-auto">
        {data && (
          <>
            {data.length === 0 ? (
              <NoInvitePartners className="h-full pb-4" />
            ) : (
              data?.map((props) => (
                <FriendDataRow key={props.telegramId} {...props} />
              ))
            )}
          </>
        )}
      </div>

      <div className="w-full h-16 relative">
        {data?.length === 0 && (
          <Invite className="!w-full mt-auto absolute top-0 left-1/2 -translate-x-1/2" />
        )}
        {data && !!data.length && data.length % 10 === 0 && (
          <MainButton
            disabled={isLoading}
            isLoading={isLoading}
            onClick={showMore}
            text={t('Show more')}
            className="my-4"
          />
        )}
      </div>
    </div>
  );
};

export { FriendsTable };
